import { useState, forwardRef, useImperativeHandle } from 'react';
import { Stack, Text, SegmentedControl, TextInput, Chip, Group, Button, Popover, ActionIcon, Divider } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { ProductPreferences } from '../../types/familyProfile';

type ProductPreferenceProps = {
  value: ProductPreferences;
  onChange: (value: ProductPreferences) => void;
};

export type ProductPreferenceRef = {
  flushInputs: () => void;
};

const ProductPreferenceComponent = forwardRef<ProductPreferenceRef, ProductPreferenceProps>(
  ({ value, onChange }, ref) => {
    // Product preferences state
    const [newPreferredBrand, setNewPreferredBrand] = useState('');
    const [newAvoidBrand, setNewAvoidBrand] = useState('');

    useImperativeHandle(ref, () => ({
      flushInputs: () => {
        let newValue = { ...(value || {}) };
        let updated = false;

        // Flush preferred brands input
        if (newPreferredBrand.trim()) {
          const newBrands = newPreferredBrand
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item =>
              item.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
            )
            .filter(item => !(newValue.specificBrands || []).some(b => b.toLowerCase() === item.toLowerCase()));

          if (newBrands.length > 0) {
            newValue.specificBrands = [...(newValue.specificBrands || []), ...newBrands];
            newValue.avoidBrands = (newValue.avoidBrands || []).filter(avoid =>
              !newBrands.some(newBrand => newBrand.toLowerCase() === avoid.toLowerCase())
            );
            updated = true;
            setNewPreferredBrand('');
          }
        }

        // Flush avoid brands input
        if (newAvoidBrand.trim()) {
          const newBrands = newAvoidBrand
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item =>
              item.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
            )
            .filter(item => !(newValue.avoidBrands || []).some(b => b.toLowerCase() === item.toLowerCase()));

          if (newBrands.length > 0) {
            newValue.avoidBrands = [...(newValue.avoidBrands || []), ...newBrands];
            newValue.specificBrands = (newValue.specificBrands || []).filter(preferred =>
              !newBrands.some(newBrand => newBrand.toLowerCase() === preferred.toLowerCase())
            );
            updated = true;
            setNewAvoidBrand('');
          }
        }

        if (updated) {
          onChange(newValue);
        }
      }
    }));

  // Product preference handlers
  const updateProductPreferences = (updates: Partial<ProductPreferences>) => {
    const current = value || {};
    onChange({ ...current, ...updates });
  };

  const addPreferredBrand = () => {
    if (!newPreferredBrand.trim()) return;
    
    const newBrands = newPreferredBrand
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .filter(item => !(value?.specificBrands || []).includes(item));
    
    if (newBrands.length === 0) return;
    
    updateProductPreferences({
      specificBrands: [...(value?.specificBrands || []), ...newBrands]
    });
    setNewPreferredBrand('');
  };

  const removePreferredBrand = (brand: string) => {
    updateProductPreferences({
      specificBrands: (value?.specificBrands || []).filter(b => b !== brand)
    });
  };

  const addAvoidBrand = () => {
    if (!newAvoidBrand.trim()) return;
    
    const newBrands = newAvoidBrand
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .filter(item => !(value?.avoidBrands || []).includes(item));
    
    if (newBrands.length === 0) return;
    
    updateProductPreferences({
      avoidBrands: [...(value?.avoidBrands || []), ...newBrands]
    });
    setNewAvoidBrand('');
  };

  const removeAvoidBrand = (brand: string) => {
    updateProductPreferences({
      avoidBrands: (value?.avoidBrands || []).filter(b => b !== brand)
    });
  };

  return (
    <Stack gap="lg">
      {/* Brand Preference */}
      <Stack gap="xs">
        <Text fw={500}>Brand preference</Text>

        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.brandPreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            brandPreference: val as 'store' | 'name' | 'no_preference' 
          })}
          data={[
            {
              label: 'No preference',
              value: 'no_preference'
            },
            {
              label: 'Store brands',
              value: 'store'
            },
            {
              label: 'Name brands',
              value: 'name'
            },
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
        
        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value?.brandPreference || 'no_preference'}
          onChange={(val) => updateProductPreferences({ brandPreference: val as any })}
          data={[
            { label: 'No preference', value: 'no_preference' },
            { label: 'Store brand', value: 'store' },
            { label: 'Name brand', value: 'name' }
          ]}
          size="md"
          color="orange"
          visibleFrom="sm"
          fullWidth
          styles={{
            root: {
              background: 'transparent'
            },
            control: {
              padding: '8px 12px'
            }
          }}
        />

      </Stack>

      <Divider />

      {/* Organic Preference */}
      <Stack gap="xs">
        <Text fw={500}>Organic preference</Text>

        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.organicPreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            organicPreference: val as 'prefer_organic' | 'no_preference' 
          })}
          data={[
            {
              label: 'No preference',
              value: 'no_preference'
            },
            {
              label: 'Prefer organic',
              value: 'prefer_organic'
            },
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />

        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value?.organicPreference || 'no_preference'}
          onChange={(val) => updateProductPreferences({ organicPreference: val as any })}
          data={[
            { label: 'No preference', value: 'no_preference' },
            { label: 'Prefer organic', value: 'prefer_organic' }
          ]}
          size="md"
          color="orange"
          visibleFrom="sm"
          fullWidth
          styles={{
            root: {
              background: 'transparent'
            },
            control: {
              padding: '8px 12px'
            }
          }}
        />

      </Stack>

      <Divider />

      {/* FoodHealth Score Preference */}
      <Stack gap="xs">
        {/* Inline icon glued to the last word: wraps with "preference"
            instead of dropping below the label on narrow screens */}
        <Text fw={500}>
          FoodHealth Score{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            preference
            <Popover width={260} position="bottom" withArrow shadow="md">
              <Popover.Target>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  aria-label="What is FoodHealth Score?"
                  style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 6 }}
                >
                  <IconInfoCircle size={14} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="xs" c="gray.7" style={{ lineHeight: 1.4 }}>
                  The <a href="https://www.foodhealth.co" target="_blank" rel="noreferrer" style={{ color: 'var(--mantine-color-blue-6)' }}>FoodHealth Score</a> rates products from 0–100 based on nutritional quality. When enabled, we'll favor higher-scoring products when choosing between similar options.
                </Text>
              </Popover.Dropdown>
            </Popover>
          </span>
        </Text>

        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.foodHealthPreference || 'no_preference'}
          onChange={(val) => onChange({
            ...value,
            foodHealthPreference: val as 'prefer_higher' | 'no_preference'
          })}
          data={[
            { label: 'No preference', value: 'no_preference' },
            { label: 'Prefer higher scores', value: 'prefer_higher' }
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />

        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value?.foodHealthPreference || 'no_preference'}
          onChange={(val) => updateProductPreferences({ foodHealthPreference: val as any })}
          data={[
            { label: 'No preference', value: 'no_preference' },
            { label: 'Prefer higher scores', value: 'prefer_higher' }
          ]}
          size="md"
          color="orange"
          visibleFrom="sm"
          fullWidth
          styles={{
            root: {
              background: 'transparent'
            },
            control: {
              padding: '8px 12px'
            }
          }}
        />

      </Stack>

      <Divider />

      {/* Price Preference */}
      <Stack gap="xs">
        <Text fw={500}>Price preference</Text>

        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.pricePreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            pricePreference: val as 'value' | 'mid_range' | 'premium' | 'no_preference' 
          })}
          data={[
            {
              label: 'No preference',
              value: 'no_preference'
            },
            {
              label: 'Value',
              value: 'value'
            },
            {
              label: 'Mid-range',
              value: 'mid_range'
            },
            {
              label: 'Premium',
              value: 'premium'
            },
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />

        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value?.pricePreference || 'no_preference'}
          onChange={(val) => updateProductPreferences({ pricePreference: val as any })}
          data={[
            { label: 'No preference', value: 'no_preference' },
            { label: 'Value', value: 'value' },
            { label: 'Mid-range', value: 'mid_range' },
            { label: 'Premium', value: 'premium' }
          ]}
          size="md"
          color="orange"
          visibleFrom="sm"
          fullWidth
          styles={{
            root: {
              background: 'transparent'
            },
            control: {
              padding: '8px 12px'
            }
          }}
        />

      </Stack>

      {/* Preferred Brands */}
      <Stack gap="xs">
        <Text fw={500}>Preferred brands</Text>
        <Group gap="xs">
          <TextInput
            placeholder="Add preferred brands..."
            value={newPreferredBrand}
            onChange={(e) => setNewPreferredBrand(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addPreferredBrand()}
            style={{ flex: 1 }}
            size="md"
            styles={{
              input: {
                fontSize: '16px',
                padding: '12px 16px'
              }
            }}
          />
          <Button size="sm" onClick={addPreferredBrand} disabled={!newPreferredBrand.trim()}>
            Add
          </Button>
        </Group>
        
        {(value?.specificBrands || []).length > 0 && (
          <Group gap="xs" className="chip-cloud">
            {(value?.specificBrands || []).map(brand => (
              <Chip
                key={brand}
                checked={true}
                onChange={() => removePreferredBrand(brand)}
                variant="filled"
                color="green"
                size="sm"
              >
                {brand}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>

      {/* Avoid Brands */}
      <Stack gap="xs">
        <Text fw={500}>Brands to avoid</Text>
        <Group gap="xs">
          <TextInput
            placeholder="Add brands to avoid..."
            value={newAvoidBrand}
            onChange={(e) => setNewAvoidBrand(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addAvoidBrand()}
            style={{ flex: 1 }}
            size="md"
            styles={{
              input: {
                fontSize: '16px',
                padding: '12px 16px'
              }
            }}
          />
          <Button size="sm" onClick={addAvoidBrand} disabled={!newAvoidBrand.trim()} color="red">
            Add
          </Button>
        </Group>
        
        {(value?.avoidBrands || []).length > 0 && (
          <Group gap="xs" className="chip-cloud">
            {(value?.avoidBrands || []).map(brand => (
              <Chip
                key={brand}
                checked={true}
                onChange={() => removeAvoidBrand(brand)}
                variant="filled"
                color="red"
                size="sm"
              >
                {brand}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>
    </Stack>
  );
});

ProductPreferenceComponent.displayName = 'ProductPreference';

export const ProductPreference = ProductPreferenceComponent;
